import React, { useState, useEffect } from 'react';
import { View, ScrollView, StyleSheet, TouchableOpacity, Text, Alert, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../../contexts/AuthContext';
import { getGamificationRules } from '../../api/auth';
import ProfilePhoto from '../../components/profile/ProfilePhoto';
import { getProfileImageUrl } from '../../utils/imageUtils';
import BasicInfoForm from '../../components/profile/BasicInfoForm';
import StudentForm from '../../components/profile/StudentForm';
import EmploymentForm from '../../components/profile/EmploymentForm';
import BusinessForm from '../../components/profile/BusinessForm';
import { useTheme, getShadow } from '../../theme/useTheme';

const EditProfileScreen = ({ route }) => {
  const showWelcome = route?.params?.showWelcome;
  const navigation = useNavigation();
  const { user, updateUser } = useAuth();
  const { colors, spacing, borderRadius } = useTheme();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [rewardPoints, setRewardPoints] = useState(500); // Default placeholder
  
  useEffect(() => {
    if (showWelcome) {
      const fetchRules = async () => {
        try {
          const rulesResult = await getGamificationRules();
          if (rulesResult.success && Array.isArray(rulesResult.data)) {
            const profileRule = rulesResult.data.find(r => r.key === 'GAMIF_PROFILE_COMPLETE');
            if (profileRule && profileRule.value) {
              setRewardPoints(parseInt(profileRule.value));
            }
          }
        } catch (err) {
          console.warn('[EditProfile] Failed to fetch reward points rule:', err);
        }
      };
      fetchRules();
    }
  }, [showWelcome]);

  const [profileData, setProfileData] = useState({
    profileImage: null,
    membershipStatus: 'active',
    educationLevel: '',
    occupation: '',
    schoolDetails: {},
    employmentDetails: {},
    businessDetails: {},
    businessPhotos: []
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        profileImage: getProfileImageUrl(user.profileImageUrl || user.profileImage) || null,
        membershipStatus: user.membershipStatus || 'active',
        educationLevel: user.educationLevel || '',
        occupation: user.occupation || '',
        schoolDetails: user.schoolDetails || {},
        employmentDetails: user.employmentDetails || {},
        businessDetails: user.businessDetails || {},
        businessPhotos: user.businessPhotos || []
      });
    }
  }, [user]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setErrors({});

      if (loading) return;

      const newErrors = {};
      if (!profileData.educationLevel) {
        newErrors.educationLevel = 'Education level is required';
      }
      if (!profileData.occupation) {
        newErrors.occupation = 'Occupation is required';
      }

      if (profileData.occupation === 'student' && !profileData.schoolDetails?.schoolName) {
        newErrors.schoolName = 'School name is required';
      }
      if (profileData.occupation === 'employed' && !profileData.employmentDetails?.companyName) {
        newErrors.companyName = 'Company name is required';
      }
      if (profileData.occupation === 'business' && !profileData.businessDetails?.businessName) {
        newErrors.businessName = 'Business name is required';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setLoading(false);
        return;
      }

      const dataToSubmit = {
        ...profileData,
        schoolDetails: profileData.occupation === 'student' ?
          JSON.stringify(profileData.schoolDetails) : null,
        employmentDetails: profileData.occupation === 'employed' ?
          JSON.stringify(profileData.employmentDetails) : null,
        businessDetails: profileData.occupation === 'business' ?
          JSON.stringify(profileData.businessDetails) : null,
      };

      await updateUser(dataToSubmit);
      Alert.alert('Success', 'Profile updated successfully', [
        {
          text: 'OK',
          onPress: () => navigation.navigate('ViewProfile')
        }
      ]);
    } catch (error) {
      console.error('Profile update error:', error);
      Alert.alert('Error', error.message || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const handleOccupationChange = (type) => {
    setProfileData(prev => ({
      ...prev,
      occupation: type,
      schoolDetails: type === 'student' ? prev.schoolDetails : {},
      employmentDetails: type === 'employed' ? prev.employmentDetails : {},
      businessDetails: type === 'business' ? prev.businessDetails : {},
      businessPhotos: []
    }));
  };

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={[colors.primary.dark, colors.primary.main, colors.primary.light]}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.headerBackground}
      />

      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#FFF" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Edit Profile</Text>
        </View>

        {showWelcome && (
          <View style={styles.welcomeBanner}>
            <LinearGradient
              colors={['#4F46E5', '#6366F1']}
              style={styles.welcomeGradient}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
            >
              <View style={styles.welcomeIconCircle}>
                <Ionicons name="gift" size={24} color="#FFF" />
              </View>
              <View style={styles.welcomeTextColumn}>
                <Text style={styles.welcomeTitle}>Welcome to the Community! 🎁</Text>
                <Text style={styles.welcomeSubtitle}>
                  Complete your profile details to earn <Text style={{ fontWeight: '800', color: '#FBBF24' }}>{rewardPoints} Bonus Points</Text> and unlock exclusive features.
                </Text>
              </View>
            </LinearGradient>
          </View>
        )}

        <View style={styles.formCard}>
          <View style={styles.photoSection}>
            <ProfilePhoto
              image={profileData.profileImage}
              onImageSelect={(imageData) => {
                setProfileData(prev => ({ ...prev, profileImage: imageData }));
              }}
              error={errors.profileImage}
            />
          </View>

          <View style={styles.sectionHeader}>
            <Ionicons name="person-circle-outline" size={20} color={colors.primary.main} />
            <Text style={styles.sectionLabel}>Account Details</Text>
          </View>

          <BasicInfoForm
            membershipStatus={profileData.membershipStatus}
            educationLevel={profileData.educationLevel}
            onMembershipChange={(status) =>
              setProfileData(prev => ({ ...prev, membershipStatus: status }))}
            onEducationChange={(level) =>
              setProfileData(prev => ({ ...prev, educationLevel: level }))}
            errors={errors}
          />

          <View style={[styles.sectionHeader, { marginTop: 8 }]}>
            <Ionicons name="briefcase-outline" size={20} color={colors.primary.main} />
            <Text style={styles.sectionLabel}>Occupation & Status</Text>
          </View>

          <View style={styles.occupationSelector}>
            {[
              { id: 'student', label: 'Student', icon: 'school' },
              { id: 'employed', label: 'Employed', icon: 'business' },
              { id: 'business', label: 'Business', icon: 'storefront' }
            ].map((type) => (
              <TouchableOpacity
                key={type.id}
                style={[
                  styles.chip,
                  profileData.occupation === type.id && { backgroundColor: colors.primary.main, borderColor: colors.primary.main }
                ]}
                onPress={() => handleOccupationChange(type.id)}
              >
                <Ionicons
                  name={type.icon + (profileData.occupation === type.id ? "" : "-outline")}
                  size={16}
                  color={profileData.occupation === type.id ? "#FFF" : colors.text.secondary}
                />
                <Text style={[
                  styles.chipText,
                  profileData.occupation === type.id && styles.chipTextActive
                ]}>
                  {type.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          {errors.occupation && (
            <Text style={styles.errorText}>{errors.occupation}</Text>
          )}

          {profileData.occupation && (
            <View style={styles.occupationDetails}>
              {profileData.occupation === 'student' && (
                <StudentForm
                  schoolDetails={profileData.schoolDetails}
                  onSchoolDetailsChange={(details) =>
                    setProfileData(prev => ({ ...prev, schoolDetails: details }))}
                  errors={errors}
                />
              )}

              {profileData.occupation === 'employed' && (
                <EmploymentForm
                  employmentDetails={profileData.employmentDetails}
                  onEmploymentDetailsChange={(details) =>
                    setProfileData(prev => ({ ...prev, employmentDetails: details }))}
                  errors={errors}
                />
              )}

              {profileData.occupation === 'business' && (
                <BusinessForm
                  businessDetails={profileData.businessDetails}
                  onBusinessDetailsChange={(details) =>
                    setProfileData(prev => ({ ...prev, businessDetails: details }))}
                  businessPhotos={profileData.businessPhotos}
                  onBusinessPhotosChange={(photos) =>
                    setProfileData(prev => ({ ...prev, businessPhotos: photos }))}
                  errors={errors}
                />
              )}
            </View>
          )}

          <TouchableOpacity
            style={styles.saveButton}
            onPress={handleSubmit}
            disabled={loading}
          >
            <LinearGradient
              colors={[colors.primary.main, colors.primary.dark]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 0 }}
              style={[styles.saveButtonGradient, loading && { opacity: 0.7 }]}
            >
              {loading ? (
                <Text style={styles.saveButtonText}>Updating...</Text>
              ) : (
                <>
                  <Ionicons name="cloud-upload-outline" size={20} color="#FFF" style={{ marginRight: 8 }} />
                  <Text style={styles.saveButtonText}>Save Changes</Text>
                </>
              )}
            </LinearGradient>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#EEF2FF', // Deeper, more dynamic Indigo tint
  },
  headerBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: Platform.OS === 'ios' ? 180 : 150,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 32,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: Platform.OS === 'ios' ? 50 : 35,
    paddingHorizontal: 18,
    marginBottom: 15,
  },
  backButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#FFF',
    marginLeft: 12,
  },
  formCard: {
    backgroundColor: '#FFF',
    marginHorizontal: 16,
    borderRadius: 18,
    padding: 16,
    ...getShadow('lg'),
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.8)',
  },
  photoSection: {
    alignItems: 'center',
    marginTop: -45,
    marginBottom: 15,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1E293B',
    marginLeft: 8,
  },
  occupationSelector: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: 10,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    backgroundColor: '#F1F5F9',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  chipText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748B',
    marginLeft: 4,
  },
  chipTextActive: {
    color: '#FFF',
  },
  occupationDetails: {
    marginTop: 8,
    backgroundColor: '#F1F5FE', // Slightly lighter tint for nested section
    borderRadius: 14,
    paddingHorizontal: 14,
    paddingTop: 14,
    paddingBottom: 4,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  saveButton: {
    marginTop: 20,
    borderRadius: 12,
    overflow: 'hidden',
    ...getShadow('md'),
  },
  saveButtonGradient: {
    flexDirection: 'row',
    paddingVertical: 13,
    justifyContent: 'center',
    alignItems: 'center',
  },
  saveButtonText: {
    color: '#FFF',
    fontSize: 14,
    fontWeight: '700',
  },
  errorText: {
    color: '#EF4444',
    fontSize: 11,
    marginTop: 4,
    marginLeft: 4,
    fontWeight: '500',
  },
  welcomeBanner: {
    marginHorizontal: 16,
    marginBottom: 16,
    borderRadius: 16,
    overflow: 'hidden',
    elevation: 4,
    shadowColor: '#4F46E5',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 8,
  },
  welcomeGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  welcomeIconCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  welcomeTextColumn: {
    flex: 1,
  },
  welcomeTitle: {
    color: '#FFF',
    fontSize: 15,
    fontWeight: '800',
    marginBottom: 4,
  },
  welcomeSubtitle: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 12,
    lineHeight: 16,
  },
});

export default EditProfileScreen;
